/**
 * AS Life UI kit — Tamagui ports of the handoff design-system primitives.
 * Corporate geometry (radius 5/7), card shadow, semantic tones.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Image } from 'react-native'
import { Stack, Text, XStack, YStack, styled, type GetProps } from 'tamagui'
import Svg, { Circle } from 'react-native-svg'

import { avColor, initials } from '../lib/avatar'
import { tone as toneOf, type Tone } from '../theme/tones'
import { palette } from '../theme/palette'

// ── Клавиатурная активация (веб) ─────────────────────────────────────────────
/**
 * Наши «кнопки» — это XStack/Text с onPress, т.е. на вебе <div>/<span>: без
 * фокуса по Tab и без Enter/Space. Лечится одним пропом `tag="button"`:
 * Tamagui рендерит таким узлом настоящий <button> (createComponent, ветка
 * isTaggable), а onPress вешает как DOM onClick (getWebEvents) — дальше фокус
 * и активация Enter/Space достаются от браузера бесплатно, своей обработки
 * клавиш не нужно.
 *
 * ПОЧЕМУ не onKeyDown: Tamagui не доносит его до DOM-узла — проп проходит
 * тайпчек, но обработчик не вызывается (проверено вживую в браузере).
 *
 * На нативе `tag` игнорируется: в createComponent он под isWeb-гейтом, RN
 * получает прежний View + onPress.
 *
 * Дефолтные UA-стили <button> (buttonface-фон, outset-рамка, padding 1px 6px)
 * гасим здесь же — иначе у элементов, которые не задают их сами (IconButton,
 * неактивный Seg), появляется лишняя рамка-коробка. Свои значения примитивы
 * ставят ПОСЛЕ спреда и потому выигрывают. Шрифт сбрасывать не нужно: текст
 * лежит во вложенном <span> от Text со своими классами.
 */
export const pressable = {
  tag: 'button',
  backgroundColor: 'transparent',
  borderWidth: 0,
  padding: 0,
  // UA-стиль <button> центрирует текст (text-align: center) — наследуется в
  // flex-растянутые Text-ноды (лейблы сайдбара и т.п.). Сброс — сырым style:
  // в styled-конфиг Stack textAlign не кладётся (text-only проп, уйдёт в DOM-атрибут).
  style: { textAlign: 'left' as const },
  // outline поверх, а не border: не сдвигает layout и не спорит с рамкой самого
  // элемента. focusVisible (а не focus) — кольцо только при клавиатуре.
  focusVisibleStyle: { outlineColor: '$primary', outlineWidth: 2, outlineStyle: 'solid', outlineOffset: 2 },
} as const

// ── Card ────────────────────────────────────────────────────────────────────
export const Card = styled(YStack, {
  name: 'Card',
  backgroundColor: '$background',
  borderWidth: 1,
  borderColor: '$borderSecondary',
  borderRadius: 7,
  shadowColor: palette.shadow,
  shadowOpacity: 0.13,
  shadowRadius: 7,
  shadowOffset: { width: 0, height: 2 },
})

/** Card that lifts on hover (web) and gently compresses on press. */
const PressCardFrame = styled(Card, {
  name: 'PressCard',
  cursor: 'pointer',
  animation: 'quicker',
  hoverStyle: { borderColor: '$primary', y: -2, scale: 1.005, shadowOpacity: 0.2, shadowRadius: 12 },
  pressStyle: { y: 0, scale: 0.985, shadowOpacity: 0.1 },
  focusVisibleStyle: pressable.focusVisibleStyle,
  // Фон и рамку Card задаёт сам, а вот UA-паддинг <button> гасить некому.
  padding: 0,
})

/**
 * <button> — только когда карточка правда кликабельна: PressCard без onPress
 * используется и как чисто визуальная «поднимающаяся» карточка (learning.tsx),
 * а внутри такой лежат свои <Button> — вложенные button'ы невалидны в HTML.
 */
export function PressCard(props: GetProps<typeof PressCardFrame>) {
  // textAlign нельзя положить в стили Stack (это text-only проп, Tamagui уронит
  // его в DOM-атрибут), а UA-шный `button { text-align: center }` наследуется
  // внутрь и центрует описания карточек — гасим через сырой web-style.
  return (
    <PressCardFrame
      {...(props.onPress ? { tag: pressable.tag, style: { textAlign: 'left' } } : null)}
      {...props}
    />
  )
}

/**
 * `fill` — карточка тянется на всю высоту родителя (ряд сетки) вместе с телом: дети тела
 * могут занять остаток (flex 1), без пустой полосы снизу. По умолчанию — высота по содержимому.
 */
export function SectionCard({
  title,
  extra,
  children,
  pad = 16,
  fill,
  ...rest
}: { title?: ReactNode; extra?: ReactNode; children: ReactNode; pad?: number; fill?: boolean } & GetProps<typeof Card>) {
  return (
    <Card overflow="hidden" {...(fill ? { flex: 1 } : null)} {...rest}>
      {title != null && (
        <XStack
          alignItems="center"
          justifyContent="space-between"
          paddingHorizontal={16}
          paddingVertical={13}
          borderBottomWidth={1}
          borderColor="$borderSecondary"
        >
          {typeof title === 'string' ? (
            <Text fontSize={14} fontWeight="700" color="$color">
              {title}
            </Text>
          ) : (
            title
          )}
          {extra}
        </XStack>
      )}
      <YStack padding={pad} {...(fill ? { flex: 1 } : null)}>
        {children}
      </YStack>
    </Card>
  )
}

// ── Avatar ──────────────────────────────────────────────────────────────────
const AV_SIZES = { xs: 24, sm: 30, md: 36, lg: 44, xl: 96 } as const
const STATUS_COLOR: Record<string, string> = {
  online: toneOf('green').solid,
  busy: toneOf('red').solid,
  away: toneOf('gold').solid,
  offline: '#94A3B8', // tertiary gray — static here (module scope, no theme ctx)
}

export function Avatar({
  name,
  seed,
  size = 'md',
  status,
  color,
  uri,
}: {
  name: string
  seed?: string
  size?: keyof typeof AV_SIZES | number
  status?: 'online' | 'busy' | 'away' | 'offline'
  color?: string
  /** Optional avatar image; falls back to initials when absent. */
  uri?: string | null
}) {
  const px = typeof size === 'number' ? size : AV_SIZES[size]
  const bg = color ?? avColor(seed ?? name)
  const dot = Math.max(8, Math.round(px * 0.28))
  return (
    <Stack width={px} height={px} flexShrink={0}>
      <YStack
        width={px}
        height={px}
        borderRadius={999}
        backgroundColor={bg}
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {uri ? (
          <Image source={{ uri }} style={{ width: px, height: px }} resizeMode="cover" />
        ) : (
          <Text color="#fff" fontWeight="700" fontSize={Math.round(px * 0.38)}>
            {initials(name)}
          </Text>
        )}
      </YStack>
      {status && (
        <Stack
          position="absolute"
          bottom={-1}
          right={-1}
          width={dot}
          height={dot}
          borderRadius={999}
          backgroundColor={STATUS_COLOR[status]}
          borderWidth={2}
          borderColor="$background"
        />
      )}
    </Stack>
  )
}

// ── Tag (tonal pill) ─────────────────────────────────────────────────────────
export function Tag({ tone = 'default', children }: { tone?: Tone; children: ReactNode }) {
  const c = toneOf(tone)
  return (
    <XStack
      backgroundColor={c.soft}
      borderRadius={999}
      paddingHorizontal={9}
      paddingVertical={3}
      alignSelf="flex-start"
      alignItems="center"
    >
      <Text fontSize={11} fontWeight="700" color={c.text}>
        {children}
      </Text>
    </XStack>
  )
}

/** Small solid status dot. */
export function Dot({ tone = 'default', size = 7 }: { tone?: Tone; size?: number }) {
  return <Stack width={size} height={size} borderRadius={999} backgroundColor={toneOf(tone).solid} />
}

// ── Ring (progress) ──────────────────────────────────────────────────────────
export function Ring({
  size = 58,
  pct = 0,
  color,
  trackColor,
  thickness = 5,
  children,
}: {
  size?: number
  pct?: number
  color: string
  trackColor: string
  thickness?: number
  children?: ReactNode
}) {
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100)
  return (
    <Stack width={size} height={size} alignItems="center" justifyContent="center">
      <Svg width={size} height={size} style={{ position: 'absolute', transform: [{ rotate: '-90deg' }] }}>
        <Circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={thickness} fill="none" />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          fill="none"
        />
      </Svg>
      {children}
    </Stack>
  )
}

// ── Progress bar ─────────────────────────────────────────────────────────────
/**
 * Fill animates from 0 on mount and springs to any new `pct`. The fill is a
 * full-width bar translated left (transform, not width), so both drivers keep
 * it off the layout/JS path.
 */
export function ProgressBar({ pct, color = '$primary', height = 6 }: { pct: number; color?: string; height?: number }) {
  const target = Math.max(0, Math.min(100, pct))
  const [shown, setShown] = useState(0)
  useEffect(() => {
    const t = setTimeout(() => setShown(target), 60)
    return () => clearTimeout(t)
  }, [target])
  return (
    <Stack height={height} borderRadius={999} backgroundColor="$borderSecondary" overflow="hidden" width="100%">
      <Stack
        animation="lazy"
        height={height}
        borderRadius={999}
        backgroundColor={color}
        width="100%"
        scaleX={shown / 100}
        transformOrigin="left"
      />
    </Stack>
  )
}

// ── Icon button ──────────────────────────────────────────────────────────────
export const IconButton = styled(XStack, {
  name: 'IconButton',
  ...pressable,
  width: 36,
  height: 36,
  borderRadius: 7,
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  animation: 'quicker',
  hoverStyle: { backgroundColor: '$backgroundHover', scale: 1.06 },
  pressStyle: { backgroundColor: '$backgroundPress', scale: 0.9 },
})

// ── Link text ────────────────────────────────────────────────────────────────
// role="link": семантика ссылки, но носитель — <button> (см. pressable): <a> без
// href не фокусируется, а навигация тут всё равно через onPress/router.
export const Link = styled(Text, {
  name: 'Link',
  ...pressable,
  role: 'link',
  color: '$primary',
  fontWeight: '600',
  cursor: 'pointer',
  hoverStyle: { opacity: 0.8 },
})

// ── Button ───────────────────────────────────────────────────────────────────
/**
 * Pill/rounded button matching the handoff `NS.Button`.
 * `primary` = solid navy; `default` = outlined. Pass `icon` as an <Icon/> node.
 */
export function Button({
  children,
  variant = 'default',
  size = 'md',
  icon,
  block,
  disabled,
  ...rest
}: {
  children?: ReactNode
  variant?: 'primary' | 'default'
  size?: 'sm' | 'md'
  icon?: ReactNode
  block?: boolean
} & GetProps<typeof XStack>) {
  const isPrimary = variant === 'primary'
  const h = size === 'sm' ? 32 : 36
  return (
    <XStack
      {...pressable}
      // disabled Tamagui разбирает сам: не вешает обработчики (onPress не
      // вызовется) и ставит aria-disabled + нативный disabled на <button>.
      // Остаётся курсор — его Tamagui не трогает.
      disabled={disabled}
      cursor={disabled ? 'default' : 'pointer'}
      animation="quick"
      height={h}
      alignItems="center"
      justifyContent="center"
      gap={7}
      paddingHorizontal={children != null ? 14 : 10}
      borderRadius={7}
      borderWidth={isPrimary ? 0 : 1}
      borderColor="$borderColor"
      backgroundColor={isPrimary ? '$primary' : '$background'}
      hoverStyle={
        isPrimary
          ? { backgroundColor: '$primaryActive' }
          : { borderColor: '$primary', backgroundColor: '$backgroundHover' }
      }
      pressStyle={{ opacity: 0.92, scale: 0.97 }}
      {...(block ? { flex: 1, width: '100%' } : null)}
      {...rest}
    >
      {icon}
      {children != null && (
        <Text color={isPrimary ? '$onPrimary' : '$color'} fontWeight="600" fontSize={size === 'sm' ? 12 : 13}>
          {children}
        </Text>
      )}
    </XStack>
  )
}
